import { HIRE_PLAN_SESSION_KEY } from "~/lib/marketplace/hire-pricing";
import { HIRE_RECOMMENDATIONS_SESSION_KEY } from "~/lib/marketplace/hire-recommendations-pricing";
import {
  clearWebsiteSuggestion,
  HIRE_WEBSITE_SUGGESTION_KEY,
} from "~/lib/marketplace/hire-website-suggestion";
import {
  HIRE_WIZARD_CONTEXT_SESSION_KEY,
  HIRE_WIZARD_STEP_SESSION_KEY,
  clearHireWizardProgress,
} from "~/lib/marketplace/hire-wizard-steps";
import {
  clearHireCustomPreviewState,
  clearPendingHireIntentId,
  HIRE_CUSTOM_PREVIEW_SESSION_KEY,
  PENDING_HIRE_INTENT_SESSION_KEY,
} from "~/lib/marketplace/marketplace-hire-flow";
import { normalizeMarketplaceWebsiteUrl } from "~/lib/marketplace/website-suggestions";
import { clearConsultantState } from "~/lib/siteglue/consultant-onboarding";
import { SITEGLUE_ONBOARD_SESSION_KEY } from "~/lib/siteglue/onboard-session";
import { SITEGLUE_ONBOARDING_SESSION_KEY } from "~/lib/org-onboarding/track-onboarding-session";

const TRAINING_KEY = "egpt-hire-wizard-training";
const TOOLS_KEY = "egpt-hire-wizard-tool-credentials";

/** Role / intent captured from the marketing home hero before website collection. */
export const HIRE_ONBOARDING_INITIAL_PROMPT_KEY = "egpt-hire-onboarding-initial-prompt";

/** Persisted Inbound GPT Builder chat messages for the hire custom flow. */
export const HIRE_BUILDER_CHAT_SESSION_KEY = "egpt-hire-builder-chat-session";

/**
 * Pre-preview Trish onboarding transcript + step state.
 * Survives Sign Up / email verify and is carried into HireBuilderBuildChat.
 */
export const HIRE_TRISH_ONBOARDING_TRANSCRIPT_KEY = "egpt-hire-trish-onboarding-transcript";

export type HireTrishOnboardingTranscriptMessage = {
  id: string;
  role: "assistant" | "user";
  content: string;
};

export type HireTrishOnboardingSavedState = {
  websiteKey: string;
  messages: HireTrishOnboardingTranscriptMessage[];
  step?: string;
  jobTitle?: string;
  roleLabel?: string;
  useCaseDescription?: string;
  agentName?: string;
  profileImageUrl?: string;
  businessName?: string;
  websiteUrl?: string | null;
  configPreference?: "free" | "recommended";
  selectedGoals?: string[];
  selectedChannels?: string[];
};

/** @deprecated alias — messages-only shape still readable */
export type HireTrishOnboardingTranscript = HireTrishOnboardingSavedState;

export function writeHireTrishOnboardingTranscript(
  websiteKey: string,
  messages: Array<{ id: string; role: "trish" | "user" | "assistant"; content: string }>,
  extras?: Omit<HireTrishOnboardingSavedState, "websiteKey" | "messages">,
): void {
  if (typeof window === "undefined" || !websiteKey) return;
  try {
    const normalized: HireTrishOnboardingTranscriptMessage[] = messages.map((m) => ({
      id: m.id,
      role: m.role === "user" ? "user" : "assistant",
      content: m.content,
    }));
    const existing = readHireTrishOnboardingSavedState(websiteKey);
    const payload: HireTrishOnboardingSavedState = {
      websiteKey,
      messages: normalized,
      ...(existing
        ? {
            step: existing.step,
            jobTitle: existing.jobTitle,
            roleLabel: existing.roleLabel,
            useCaseDescription: existing.useCaseDescription,
            agentName: existing.agentName,
            profileImageUrl: existing.profileImageUrl,
            businessName: existing.businessName,
            websiteUrl: existing.websiteUrl,
            configPreference: existing.configPreference,
            selectedGoals: existing.selectedGoals,
            selectedChannels: existing.selectedChannels,
          }
        : {}),
      ...extras,
    };
    // localStorage so email-verify / new-tab Sign Up can resume the same chat.
    localStorage.setItem(HIRE_TRISH_ONBOARDING_TRANSCRIPT_KEY, JSON.stringify(payload));
    sessionStorage.setItem(HIRE_TRISH_ONBOARDING_TRANSCRIPT_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function readHireTrishOnboardingSavedState(
  websiteKey?: string | null,
): HireTrishOnboardingSavedState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw =
      localStorage.getItem(HIRE_TRISH_ONBOARDING_TRANSCRIPT_KEY) ||
      sessionStorage.getItem(HIRE_TRISH_ONBOARDING_TRANSCRIPT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as HireTrishOnboardingSavedState;
    if (!parsed?.messages || !Array.isArray(parsed.messages)) return null;
    if (websiteKey && parsed.websiteKey && parsed.websiteKey !== websiteKey) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function readHireTrishOnboardingTranscript(
  websiteKey: string,
): HireTrishOnboardingTranscriptMessage[] {
  return readHireTrishOnboardingSavedState(websiteKey)?.messages ?? [];
}

export function clearHireTrishOnboardingTranscript(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(HIRE_TRISH_ONBOARDING_TRANSCRIPT_KEY);
    sessionStorage.removeItem(HIRE_TRISH_ONBOARDING_TRANSCRIPT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Set when the user opens /agents/hire/custom?new=1 so generate always clones a
 * fresh project instead of resuming a PENDING hire intent.
 */
export const HIRE_FORCE_NEW_AGENT_SESSION_KEY = "egpt-hire-force-new-agent";

export function markHireForceNewAgent(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(HIRE_FORCE_NEW_AGENT_SESSION_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function clearHireForceNewAgent(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(HIRE_FORCE_NEW_AGENT_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export function isHireForceNewAgent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(HIRE_FORCE_NEW_AGENT_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

export function normalizeHireWebsiteSessionKey(url: string): string {
  const normalized = normalizeMarketplaceWebsiteUrl(url);
  if (!normalized) return url.trim().toLowerCase().replace(/\/$/, "");
  try {
    const parsed = new URL(normalized);
    return `${parsed.hostname.replace(/^www\./i, "")}${parsed.pathname.replace(/\/$/, "")}`.toLowerCase();
  } catch {
    return url.trim().toLowerCase().replace(/\/$/, "");
  }
}

/** Clear hire wizard session data so a new website build starts fresh at Step 1. */
export function clearHireOnboardingSessionStorage(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(HIRE_WIZARD_STEP_SESSION_KEY);
    sessionStorage.removeItem(HIRE_WIZARD_CONTEXT_SESSION_KEY);
    sessionStorage.removeItem(HIRE_RECOMMENDATIONS_SESSION_KEY);
    sessionStorage.removeItem(TRAINING_KEY);
    sessionStorage.removeItem(TOOLS_KEY);
    sessionStorage.removeItem(HIRE_CUSTOM_PREVIEW_SESSION_KEY);
    sessionStorage.removeItem(HIRE_BUILDER_CHAT_SESSION_KEY);
    sessionStorage.removeItem(HIRE_TRISH_ONBOARDING_TRANSCRIPT_KEY);
    try {
      localStorage.removeItem(HIRE_TRISH_ONBOARDING_TRANSCRIPT_KEY);
      localStorage.removeItem(HIRE_CUSTOM_PREVIEW_SESSION_KEY);
      localStorage.removeItem(HIRE_RECOMMENDATIONS_SESSION_KEY);
      localStorage.removeItem(HIRE_BUILDER_CHAT_SESSION_KEY);
      localStorage.removeItem("egpt-hire-builder-onboarding-complete");
      localStorage.removeItem("egpt-hire-builder-onboarding-state");
      localStorage.removeItem(HIRE_WIZARD_CONTEXT_SESSION_KEY);
      localStorage.removeItem(HIRE_WIZARD_STEP_SESSION_KEY);
    } catch {
      /* ignore */
    }
    sessionStorage.removeItem(HIRE_PLAN_SESSION_KEY);
    sessionStorage.removeItem(PENDING_HIRE_INTENT_SESSION_KEY);
    sessionStorage.removeItem("egpt-hire-builder-onboarding-complete");
    sessionStorage.removeItem("egpt-hire-onboarding-goals");
    sessionStorage.removeItem("egpt-hire-builder-onboarding-state");
    sessionStorage.removeItem(HIRE_ONBOARDING_INITIAL_PROMPT_KEY);
    sessionStorage.removeItem(HIRE_FORCE_NEW_AGENT_SESSION_KEY);
    try {
      sessionStorage.removeItem("egpt-siteglue-consultant-state");
      localStorage.removeItem("egpt-siteglue-consultant-state");
      sessionStorage.removeItem(SITEGLUE_ONBOARDING_SESSION_KEY);
    } catch {
      /* ignore */
    }
  } catch {
    /* ignore */
  }
}

/** Full reset when starting a new agent build from the dashboard (+ Build new agent). */
export function resetHireBuilderForNewAgent(): void {
  clearHireOnboardingSessionStorage();
  clearHireWizardProgress();
  clearWebsiteSuggestion();
  clearHireCustomPreviewState();
  clearPendingHireIntentId();
  markHireForceNewAgent();
  clearConsultantState();
}

/**
 * Hard navigation reset for SiteGlue "Start Over" / + Build new agent.
 * Clears session storage and strips sticky URL params (`website`, `plan`, resume ids)
 * so Trish's welcome does not reuse a prior organization URL.
 */
export function navigateHireBuilderStartOver(): void {
  resetHireBuilderForNewAgent();
  try {
    // SiteGlue /onboard keeps its own session key — clear so progress resets to 0%.
    try {
      window.localStorage.removeItem(SITEGLUE_ONBOARD_SESSION_KEY);
    } catch {
      /* ignore */
    }
    const url = new URL(window.location.href);
    url.searchParams.set("new", "1");
    url.searchParams.delete("plan");
    url.searchParams.delete("website");
    url.searchParams.delete("hireIntentId");
    url.searchParams.delete("payment");
    window.location.assign(url.toString());
  } catch {
    window.location.reload();
  }
}

export function readStoredHireWebsiteKey(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(HIRE_WEBSITE_SUGGESTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { websiteUrl?: string };
    return parsed.websiteUrl ? normalizeHireWebsiteSessionKey(parsed.websiteUrl) : null;
  } catch {
    return null;
  }
}

export function hireWebsiteSessionMatches(storedUrl: string, currentUrl: string): boolean {
  return normalizeHireWebsiteSessionKey(storedUrl) === normalizeHireWebsiteSessionKey(currentUrl);
}
